// =============================================================================
// Sell-outs + End of Day — the READ side of the flip (Phase 3, module 1).
//
// Sibling of the WRITE glue in selloutSync.js. Until now every screen still READ
// the Google Sheet (?action=sellOutToday / ?action=eodToday) even though the
// tills already SHADOW-write here. This lib is the reader that lets the FOH tills
// read today's SOLD-OUT state AND the End of Day report from Supabase. EOD is
// safe to read here because the EOD-undo self-heal (reconcile_sellouts) reconciles
// a stale 'submitted' back to 'undone' against the authoritative sheet.
//
// SAFETY POSTURE — this reader changes NOTHING until it is deliberately switched
// on. `selloutReadEnabled()` is OFF for every host by default (empty allowlist);
// a flip is a one-line host add or `?selloutread=on` for a test. So merging this
// can never change what a live kiosk reads. The old sheet write stays the boss.
//
// Shapes reproduced EXACTLY so the untouched UI keeps working:
//   sellOutToday -> { sellOutTimes:{sku:"HH:MM"}, sellOutCycles:{sku:[{soldOut,restock,loggedAt}]}, date:"DD/MM/YYYY" }
//   eodToday     -> { submitted:bool, staffName?, timestamp?, date?, waste?, wasteByItem?, tgtgSold?, tgtgNotCollected? }
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { siteIdForKey, ensureAnonSession } from "./stockSync.js";
import { eodTodayShape } from "./eodTodayShape.js";

// London "today" as YYYY-MM-DD (matches the DB trading_date + selloutSync writes).
export const londonToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
// "DD/MM/YYYY" to match the sheet feed's `date` field exactly.
const londonDateUK = () => new Date().toLocaleDateString("en-GB", { timeZone: "Europe/London" });
// Postgres `time` comes back as "HH:MM:SS" — the app speaks "HH:MM".
const hhmm = (t) => (t == null ? null : String(t).slice(0, 5));
// (ymdToUK moved into eodTodayShape.js, which now owns the row -> shape mapping.)

// --- the read-flip gate (sell-outs specific) ---------------------------------
// Deliberately EMPTY by default: the reader is dormant on every host until a
// site is added here (a reviewed one-line flip) or `?selloutread=on` is passed
// (staging / pre-shift test). `?selloutread=off` force-disables. Kept SEPARATE
// from stockSync.readFlipEnabled() because that gate targets the kitchen host,
// and sell-outs flips at the FOH tills (trafford/arndale), never the kitchen.
const SELLOUT_READ_HOSTS = new Set([
  "arndale.eatrolled.com",       // FLIPPED 16 Jul — guinea-pig kiosk (has data, lower stakes)
  "trafford.eatrolled.com",      // FLIPPED 16 Jul — same proven reader + parity, self-heal armed
]);
export function selloutReadEnabled() {
  if (typeof window === "undefined") return false; // node/tests: default off
  try {
    const q = new URLSearchParams(window.location.search).get("selloutread");
    if (q === "on") return true;
    if (q === "off") return false;
  } catch (e) {}
  return SELLOUT_READ_HOSTS.has((window.location.hostname || "").toLowerCase());
}

// --- the read-flip gate (End of Day *today* banner) ---------------------------
// SEPARATE from the sell-out gate above, on purpose. The two reads were sharing
// one host list, which quietly welded two unrelated flips together:
//   * the sell-out read is a live count of ~30 individual till taps a day, and it
//     is only as good as the till capture rate (Meadowhall's is ~67% — see
//     RUNBOOK_FLIP_EOD.md), so it is genuinely not ready to flip everywhere;
//   * the EOD-today read is ONE form submission a day, dual-written, and is
//     complete wherever the day gets submitted.
// Welded together, the good flip had to wait for the bad one. Split, each moves
// when its own evidence says so.
//
// SEEDED WITH THE TWO HOSTS THAT ALREADY READ EOD FROM SUPABASE, so this change
// is behaviour-identical on the day it merges. Adding a host here is the whole
// flip. `?eodread=on|off` overrides for a pre-shift test.
const EOD_READ_HOSTS = new Set([
  "arndale.eatrolled.com",       // reading EOD-today from Supabase since 16 Jul (inherited from the sell-out gate)
  "trafford.eatrolled.com",      // ditto
  // "meadowhall.eatrolled.com", // HELD 17 Sep 2026 22:30 BST: #353 merged 22:22 but the prod pre-flight (RUNBOOK_FLIP_EOD.md) returned safe_to_flip = false — 11 of the last 27 days missing in daily_reports (21–31 Aug 2026), last 16 days complete, 0 late. Re-enable = uncomment this line after those days are backfilled and the pre-flight reads true (decision 7a, 14 Sep 2026); revert = re-comment this line
]);
export function eodReadEnabled() {
  if (typeof window === "undefined") return false; // node/tests: default off
  try {
    const q = new URLSearchParams(window.location.search).get("eodread");
    if (q === "on") return true;
    if (q === "off") return false;
  } catch (e) {}
  return EOD_READ_HOSTS.has((window.location.hostname || "").toLowerCase());
}

// =============================================================================
// PURE reconstruction (exported + exercised by sellouts.test.mjs, no network).
// Rebuilds the sheet's sellOutToday shape from the append-only sell_out_events
// log. Processed in WRITE order (created_at) — the causal order of the taps —
// per SKU:
//   sold_out -> open a new cycle {soldOut: HH:MM, restock: null}
//   restock  -> close the most-recent still-open cycle (set its restock)
//   undo     -> remove the still-open sold-out whose time == original_sold_out_time
// A roll is currently OUT iff its last cycle exists and its restock is null —
// the exact rule the screen's isOut() applies. sellOutTimes carries that open
// time (sku -> "HH:MM") for the rolls that are out right now.
// =============================================================================
export function reconstructSellOuts(events, { date = londonDateUK() } = {}) {
  const bySku = new Map();
  for (const e of events || []) {
    if (!e || !e.sku) continue;
    if (!bySku.has(e.sku)) bySku.set(e.sku, []);
    bySku.get(e.sku).push(e);
  }
  const sellOutCycles = {};
  const sellOutTimes = {};
  for (const [sku, list] of bySku) {
    // Stable write-order sort: created_at is the causal order; id is the tiebreak
    // so two events sharing an identical created_at still order deterministically
    // (the query selects id for exactly this — DB row order is otherwise undefined).
    list.sort((a, b) =>
      String(a.created_at || "").localeCompare(String(b.created_at || "")) ||
      String(a.id || "").localeCompare(String(b.id || "")));
    const cycles = [];
    for (const e of list) {
      const t = hhmm(e.event_time);
      if (e.event === "sold_out") {
        // loggedAt = the write time (created_at). The screen reads last.loggedAt
        // to decide whether the 15-min Undo button still shows (foh.jsx:3664) —
        // omit it and Undo silently disappears after the flip.
        cycles.push({ soldOut: t, restock: null, loggedAt: e.created_at || null });
      } else if (e.event === "restock") {
        for (let i = cycles.length - 1; i >= 0; i--) {
          if (cycles[i].restock == null) { cycles[i].restock = t; break; }
        }
      } else if (e.event === "undo") {
        const target = hhmm(e.original_sold_out_time);
        // remove the most-recent still-open sold-out matching the reversed time;
        // fall back to the most-recent still-open cycle if the time doesn't line up.
        // NOTE (reviewed, intentional): if a roll was somehow marked sold_out
        // twice with no restock between and only the later one is undone, an
        // earlier open cycle remains -> the roll stays SOLD OUT. That diverges
        // from the sell_out_current view's latest-event-wins (which would show
        // available), but it's the SAFE direction (never shows a sold-out roll as
        // available) and arguably more correct — the earlier sold_out still stands.
        let idx = -1, openIdx = -1;
        for (let i = cycles.length - 1; i >= 0; i--) {
          if (cycles[i].restock == null) {
            if (openIdx === -1) openIdx = i;
            if (cycles[i].soldOut === target) { idx = i; break; }
          }
        }
        const rm = idx !== -1 ? idx : openIdx;
        if (rm !== -1) cycles.splice(rm, 1);
      }
    }
    if (cycles.length) sellOutCycles[sku] = cycles;
    const last = cycles[cycles.length - 1];
    if (last && last.restock == null) sellOutTimes[sku] = last.soldOut;
  }
  return { sellOutTimes, sellOutCycles, date };
}

// =============================================================================
// Reads (network). Best-effort at the call site — the screen keeps its own .catch.
// fetchSellOutToday reads Supabase (sheet fallback on error); fetchEodToday reads
// the authoritative sheet during the soak (see its note).
// =============================================================================

// Today's sold-out picture for one site, in the sheet's sellOutToday shape.
// Supabase-first with a SHEET FALLBACK on any failure. This is customer-visible
// (it decides which rolls show sold out), and the read policy is `to authenticated`,
// so a signed-out session would return ZERO rows — indistinguishable from "nothing
// sold out". We therefore (1) ensure the anonymous session first, and (2) fall back
// to the still-authoritative sheet on ANY error rather than risk a sold-out roll
// reading as available. An EMPTY result WITH a good session is a valid state (no
// rolls out) and is returned as-is — never treated as an error.
export async function fetchSellOutToday(siteKey, { sheetUrl } = {}) {
  // Every fallback here is an ERROR path (a healthy flip reads Supabase fine), so
  // warn loudly with a [sellout-read] tag — greppable in the Cloudflare/kiosk
  // logs. Silent fallbacks would hide a Supabase read that has quietly stopped
  // working (RLS/timeout), making the flip look done when the till is really
  // still on the sheet. (Rolled has no Sentry/Datadog sink today; the reconcile
  // parity check + the staged-soak Supabase-log check are the other watchdogs.)
  const fallback = async (why) => {
    console.warn("[sellout-read] sold-out read fell back to the sheet:", why);
    if (!sheetUrl) return null; // no fallback available -> screen keeps last-good state
    // Tag the shape with _source so the screen can show a live "reading from …"
    // indicator during the soak (an at-a-glance signal the till fell back).
    try { const r = await fetch(sheetUrl); if (!r.ok) return null; const j = await r.json(); return { ...(j || {}), _source: "sheet" }; } catch (e) { return null; }
  };
  try {
    await ensureAnonSession(); // the read policy is `to authenticated`
    const siteId = await siteIdForKey(siteKey);
    if (!siteId) return fallback("no site id for " + siteKey);
    const { data, error } = await supabase
      .from("sell_out_events")
      .select("id, sku, event, event_time, original_sold_out_time, created_at")
      .eq("site_id", siteId)
      .eq("trading_date", londonToday())
      .order("created_at", { ascending: true });
    if (error) return fallback("supabase error: " + (error.message || error));
    return { ...reconstructSellOuts(data || []), _source: "supabase" };
  } catch (e) {
    return fallback("threw: " + (e?.message || e));
  }
}

// Resolve a staff id to a display name via the tight staff_name(id) function
// (migration 0019) — it resolves ONLY an id we already hold (submitted_by), with
// no way to list staff; the staff table itself stays RLS-locked. Best-effort:
// if it can't resolve, the banner shows "staff" (the UI's own fallback).
async function staffNameFor(staffId) {
  if (!staffId) return null;
  try { const { data } = await supabase.rpc("staff_name", { p_id: staffId }); return data || null; }
  catch (e) { return null; }
}

// Today's End of Day report for one site, in the sheet's eodToday shape.
// Supabase-first — safe now that the EOD-undo self-heal exists: a swallowed undo
// that leaves a STALE 'submitted' row gets reconciled to 'undone' server-side
// against the authoritative sheet (reconcile_sellouts eodHealAction), so this
// can't silently re-close a corrected day. Falls back to the sheet when Supabase
// has NO active report today (covers CC retro EODs — not shadowed here — and any
// not-yet-healed miss) so the banner never wrongly reads "not submitted", and on
// any error. Residual: up to the heal interval (~30 min) a not-yet-healed stale
// close could show; the sheet write stays the boss and the heal closes it.
export async function fetchEodToday(siteKey, { sheetUrl } = {}) {
  // isError=true means the Supabase read genuinely FAILED (warn — greppable);
  // isError=false is the NORMAL "no report yet / undone" fallback (stay quiet).
  const fallback = async (why, isError) => {
    if (isError) console.warn("[sellout-read] EOD read fell back to the sheet:", why);
    if (!sheetUrl) return { submitted: false };
    try { const r = await fetch(sheetUrl); return r.ok ? await r.json() : { submitted: false }; }
    catch (e) { return { submitted: false }; }
  };
  let siteId;
  try { await ensureAnonSession(); siteId = await siteIdForKey(siteKey); } catch (e) { return fallback("session/site error: " + (e?.message || e), true); }
  if (!siteId) return fallback("no site id for " + siteKey, true);
  let data;
  try {
    const res = await supabase
      .from("daily_reports")
      .select("status, waste_summary, waste_by_item, tgtg_sold, tgtg_not_collected, submitted_at, submitted_by, original_staff_name, trading_date")
      .eq("site_id", siteId)
      .eq("trading_date", londonToday())
      .in("status", ["submitted", "retro"])
      .order("submitted_at", { ascending: false })
      .limit(1);
    if (res.error) throw res.error;
    data = (res.data || [])[0];
  } catch (e) {
    return fallback("supabase error: " + (e?.message || e), true); // read failed — the sheet is authoritative
  }
  if (!data) return fallback("no active report in supabase", false); // NORMAL — not submitted, or undone; trust the sheet
  // A retro row is stamped submitted_by = the ADMIN who logged it; the worker who
  // actually did the day is the free-text original_staff_name. So for retro we
  // prefer the worker and only resolve submitted_by when there is no worker name;
  // for a normal submit we resolve submitted_by. Resolve the name here (network),
  // then hand the row + resolved name to the PURE shaper (eodTodayShape.js) so
  // the row -> shape mapping is unit-testable and the empty-map parity fix lives
  // in one place. Branch matches the old inline logic exactly.
  const needResolve = data.submitted_by && !(data.status === "retro" && data.original_staff_name);
  const resolvedName = needResolve ? await staffNameFor(data.submitted_by) : null;
  return eodTodayShape(data, resolvedName);
}
